import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Collapse,
    Flex,
    InputNumber,
    Progress,
    Select,
    Spin,
    Table,
    Tag,
    Typography,
} from 'antd';
import { LoadingOutlined, PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { getLotteryModalityByValue } from '../../../constants/lotteryModalidades';
import { DEFAULT_NUMBER_LEARNING_CONFIG } from '../numberLearning/config';
import { runWeightPresetBenchmark } from '../numberLearning/runWeightPresetBenchmark';
import type { DezenaRankingRow, NumberLearningConfig, NumberLearningResult } from '../numberLearning/types';
import GrauConfiancaAprendizagem from './GrauConfiancaAprendizagem';

const { Text, Paragraph } = Typography;

const INDICATOR_LABELS: Record<string, string> = {
    frequenciaHistorica: 'Frequência histórica',
    frequenciaRecente: 'Frequência recente',
    frequenciaEsperada: 'Frequência esperada',
    atrasoAtual: 'Atraso atual',
    atrasoMedio: 'Atraso médio',
    maiorAtraso: 'Maior atraso',
    menorAtraso: 'Menor atraso',
    intervaloMedio: 'Intervalo médio',
    regularidade: 'Regularidade',
    consistencia: 'Consistência',
    estabilidade: 'Estabilidade',
    tendencia: 'Tendência',
    atividade: 'Atividade',
    recencia: 'Recência',
    oscilacao: 'Oscilação',
    persistencia: 'Persistência',
    ausencia: 'Ausência',
    desvioPadrao: 'Desvio padrão',
    coeficienteVariacao: 'Coeficiente de variação',
    zScore: 'Z-Score',
    percentil: 'Percentil',
    indiceHistoricoGeral: 'Índice histórico geral',
};

interface AprendizagemNumerosProps {
    historico: number[][];
    modality: string;
}

const AprendizagemNumeros = ({ historico, modality }: AprendizagemNumerosProps) => {
    const [config, setConfig] = useState<NumberLearningConfig>(DEFAULT_NUMBER_LEARNING_CONFIG);
    const [result, setResult] = useState<NumberLearningResult | null>(null);
    const [processing, setProcessing] = useState(false);
    const [scoreMin, setScoreMin] = useState<number | null>(null);
    const [tendenciaFilter, setTendenciaFilter] = useState<string | null>(null);
    const [atividadeFilter, setAtividadeFilter] = useState<string | null>(null);

    const modalityInfo = getLotteryModalityByValue(modality);
    const hasHistorico = Boolean(historico?.length);
    const canProcess = hasHistorico && Boolean(modalityInfo);

    useEffect(() => {
        setResult(null);
    }, [historico, modality]);

    const handleProcess = () => {
        if (!modalityInfo || !hasHistorico || processing) {
            return;
        }

        setProcessing(true);

        window.setTimeout(() => {
            try {
                const benchmark = runWeightPresetBenchmark(
                    historico,
                    modalityInfo.qty_disp,
                    modalityInfo.qty_result,
                    config
                );

                setResult(benchmark?.result ?? null);
            } finally {
                setProcessing(false);
            }
        }, 0);
    };

    const filteredRows = useMemo(() => {
        if (!result) {
            return [];
        }

        return result.rows.filter((row) => {
            if (scoreMin !== null && row.score < scoreMin) {
                return false;
            }
            if (tendenciaFilter && row.tendenciaLabel !== tendenciaFilter) {
                return false;
            }
            if (atividadeFilter && row.atividadeLabel !== atividadeFilter) {
                return false;
            }
            return true;
        });
    }, [result, scoreMin, tendenciaFilter, atividadeFilter]);

    const topSugestoes = filteredRows.slice(0, modalityInfo?.qty_result ?? 6);

    const columns = [
        {
            title: 'Pos.',
            dataIndex: 'rank',
            key: 'rank',
            width: 60,
            sorter: (a: DezenaRankingRow, b: DezenaRankingRow) => a.rank - b.rank,
        },
        {
            title: 'Dezena',
            dataIndex: 'dezena',
            key: 'dezena',
            width: 80,
            sorter: (a: DezenaRankingRow, b: DezenaRankingRow) => a.dezena - b.dezena,
            render: (value: number) => String(value).padStart(2, '0'),
        },
        {
            title: 'Score',
            dataIndex: 'score',
            key: 'score',
            width: 140,
            defaultSortOrder: 'descend' as const,
            sorter: (a: DezenaRankingRow, b: DezenaRankingRow) => a.score - b.score,
            render: (score: number) => (
                <Flex vertical gap={2}>
                    <Text strong>{score.toFixed(1)}</Text>
                    <Progress percent={score} size="small" showInfo={false} />
                </Flex>
            ),
        },
        {
            title: 'Percentil',
            dataIndex: 'classificacaoPercentil',
            key: 'classificacaoPercentil',
            width: 110,
        },
        {
            title: 'Tendência',
            dataIndex: 'tendenciaLabel',
            key: 'tendenciaLabel',
            width: 100,
        },
        {
            title: 'Atividade',
            dataIndex: 'atividadeLabel',
            key: 'atividadeLabel',
            width: 110,
        },
        {
            title: 'Classificações',
            key: 'estrelas',
            render: (_: unknown, row: DezenaRankingRow) => (
                <Flex vertical gap={2}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Freq. {row.estrelas.frequencia}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>Rec. {row.estrelas.recencia}</Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>Reg. {row.estrelas.regularidade}</Text>
                </Flex>
            ),
        },
    ];

    if (!modalityInfo) {
        return <Alert type="warning" showIcon message="Selecione uma modalidade para calcular a aprendizagem." />;
    }

    return (
        <Flex vertical gap="middle" style={{ width: '100%' }}>
            <Alert
                type="info"
                showIcon
                message="Aprendizagem de números"
                description={
                    <Paragraph style={{ marginBottom: 0 }}>
                        Ranking estatístico por dezena com base exclusivamente no histórico importado.
                        Ao processar, são testadas automaticamente 10 combinações de pesos definidas em{' '}
                        <Text code>numberLearning/weightPresets.json</Text>. Apenas a combinação com
                        melhor desempenho em acertos nos menores percentuais do ranking é exibida.
                    </Paragraph>
                }
            />

            <Flex wrap gap="middle" align="center">
                <Button
                    type="primary"
                    icon={result ? <ReloadOutlined /> : <PlayCircleOutlined />}
                    loading={processing}
                    disabled={!canProcess || processing}
                    onClick={handleProcess}
                >
                    {result ? 'Reprocessar aprendizagem' : 'Processar aprendizagem'}
                </Button>
                {hasHistorico && (
                    <Tag color="blue">{historico.length} concursos no histórico</Tag>
                )}
                {!hasHistorico && (
                    <Text type="secondary">Importe o histórico para habilitar o processamento.</Text>
                )}
            </Flex>

            <Collapse
                items={[
                    {
                        key: 'config',
                        label: 'Configurações gerais',
                        children: (
                            <Flex vertical gap="middle">
                                <Text type="secondary">
                                    Os pesos são definidos no arquivo JSON de presets e testados automaticamente.
                                </Text>
                                <Flex wrap gap="middle">
                                    <Flex vertical gap={4}>
                                        <Text type="secondary">Tamanho do bloco (estabilidade)</Text>
                                        <InputNumber
                                            min={10}
                                            value={config.blockSize}
                                            onChange={(value) =>
                                                setConfig((prev) => ({
                                                    ...prev,
                                                    blockSize: Number(value) || prev.blockSize,
                                                }))
                                            }
                                        />
                                    </Flex>
                                    <Flex vertical gap={4}>
                                        <Text type="secondary">Máx. concursos analisados</Text>
                                        <InputNumber
                                            min={0}
                                            placeholder="Todos"
                                            value={config.maxConcursosAnalisados ?? undefined}
                                            onChange={(value) =>
                                                setConfig((prev) => ({
                                                    ...prev,
                                                    maxConcursosAnalisados: value ? Number(value) : null,
                                                }))
                                            }
                                        />
                                    </Flex>
                                    <Flex vertical gap={4}>
                                        <Text type="secondary">Sorteios para confiança</Text>
                                        <InputNumber
                                            min={1}
                                            value={config.validationDraws}
                                            onChange={(value) =>
                                                setConfig((prev) => ({
                                                    ...prev,
                                                    validationDraws: Number(value) || prev.validationDraws,
                                                }))
                                            }
                                        />
                                    </Flex>
                                </Flex>
                                <Text type="secondary">
                                    Janelas recentes: {config.recentWindows.join(', ')} concursos
                                </Text>
                            </Flex>
                        ),
                    },
                ]}
            />

            <Spin
                spinning={processing}
                tip="Testando 10 combinações de pesos..."
                indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            >
                <Flex vertical gap="middle" style={{ width: '100%', minHeight: processing ? 240 : undefined }}>
                    {!result && !processing && (
                        <Alert
                            type="warning"
                            showIcon
                            message="Nenhum resultado processado"
                            description="Configure os parâmetros e clique em Processar aprendizagem para gerar o ranking."
                        />
                    )}

                    {result && (
                        <>
                            {result.selectedPreset && (
                                <Alert
                                    type="info"
                                    showIcon
                                    message={`Combinação selecionada: ${result.selectedPreset.name}`}
                                    description={
                                        <>
                                            {result.selectedPreset.description}
                                            {' '}
                                            ({result.presetsTested ?? 10} presets testados — melhor
                                            acerto nos menores percentuais do ranking)
                                        </>
                                    }
                                />
                            )}

                            <Alert
                                type="success"
                                showIcon
                                message="Sugestão para o próximo sorteio"
                                description={
                                    <Flex wrap gap={8}>
                                        {topSugestoes.map((row) => (
                                            <Tag key={row.dezena} color="blue" style={{ fontSize: 14, padding: '4px 10px' }}>
                                                {String(row.dezena).padStart(2, '0')} ({row.score.toFixed(1)})
                                            </Tag>
                                        ))}
                                    </Flex>
                                }
                            />

                            <Flex wrap gap="middle">
                                <Flex vertical gap={4}>
                                    <Text type="secondary">Score mínimo</Text>
                                    <InputNumber min={0} max={100} value={scoreMin ?? undefined} onChange={(v) => setScoreMin(v ?? null)} />
                                </Flex>
                                <Flex vertical gap={4}>
                                    <Text type="secondary">Tendência</Text>
                                    <Select
                                        allowClear
                                        style={{ width: 140 }}
                                        placeholder="Todas"
                                        value={tendenciaFilter ?? undefined}
                                        onChange={(v) => setTendenciaFilter(v ?? null)}
                                        options={[
                                            { value: 'Crescente', label: 'Crescente' },
                                            { value: 'Estável', label: 'Estável' },
                                            { value: 'Decrescente', label: 'Decrescente' },
                                        ]}
                                    />
                                </Flex>
                                <Flex vertical gap={4}>
                                    <Text type="secondary">Atividade</Text>
                                    <Select
                                        allowClear
                                        style={{ width: 160 }}
                                        placeholder="Todas"
                                        value={atividadeFilter ?? undefined}
                                        onChange={(v) => setAtividadeFilter(v ?? null)}
                                        options={[
                                            { value: 'Muito alta', label: 'Muito alta' },
                                            { value: 'Alta', label: 'Alta' },
                                            { value: 'Normal', label: 'Normal' },
                                            { value: 'Baixa', label: 'Baixa' },
                                            { value: 'Muito baixa', label: 'Muito baixa' },
                                        ]}
                                    />
                                </Flex>
                                <Tag color="blue">{result.totalConcursos} concursos analisados</Tag>
                            </Flex>

                            <Table
                                style={{ width: '100%' }}
                                bordered
                                size="small"
                                rowKey="dezena"
                                pagination={false}
                                columns={columns}
                                dataSource={filteredRows}
                                expandable={{
                                    expandedRowRender: (row) => (
                                        <Flex vertical gap={4}>
                                            {Object.entries(row.indicators).map(([key, indicator]) => (
                                                <Text key={key} style={{ fontSize: 12 }}>
                                                    <strong>{INDICATOR_LABELS[key] ?? key}:</strong>{' '}
                                                    {indicator.normalized.toFixed(1)}/100
                                                    {indicator.detail ? ` — ${indicator.detail}` : ''}
                                                </Text>
                                            ))}
                                        </Flex>
                                    ),
                                }}
                            />

                            <GrauConfiancaAprendizagem
                                confidence={result.confidence}
                                numSorteados={modalityInfo?.qty_result ?? 6}
                            />
                        </>
                    )}
                </Flex>
            </Spin>
        </Flex>
    );
};

export default AprendizagemNumeros;
